// Pure helpers over the /api/artists board payload (hooks/useBoardData.js),
// shared by the leaderboard (artists_table.jsx) and the homepage (Home.jsx,
// through home/sceneModel.js: the local top 5, co-signs and "Trending across
// the US"), so both pages compute Motion, % change and weekly movers the
// same way.
import {
  parseArtistHeading,
  getFilteredDataForTimeline,
} from './artistsTableUtils';

// % change for 2D / 1W / 1M comes precomputed from the daily collector as
// profile.nguage_change, like-for-like: both ends are rescored from only the
// stats present at both, from the same source, so a platform being added,
// removed or re-sourced in between is never counted as growth. Returns
// undefined when there's no like-for-like value for this range (1Y / ALL, or
// data from before the collector produced it) - the caller falls back to
// comparing stored values - or null when the range has no comparable data.
export const likeForLikeChange = (profile, timeline) => {
  const changes = profile && profile.nguage_change;
  if (!changes || typeof changes !== 'object' || !(timeline in changes)) return undefined;
  const c = changes[timeline];
  return c && typeof c.pct === 'number' ? c : null;
};

// Fallback guards for the stored-value comparison (1Y / ALL), which can't
// tell a newly added platform from growth: a single-platform artist or a tiny
// starting NGuage posts wild swings that shouldn't read as movement. The
// like-for-like path doesn't need them - small artists' real growth shows.
const MIN_BASELINE_NGUAGE = 5;   // below this starting value, % change is noise
const MIN_CHANGE_SIGNALS = 2;    // need >=2 contributing signals to trust a trend

// Every 'YYYY_MM_DD HH:MM' date-time key across daily_update, sorted.
export function collectDateTimes(data) {
  return Array.from(new Set(Object.values(data || {}).flatMap((artist) => Object.keys(artist || {}))))
    .filter((dt) => /(\d{4})_(\d{2})_(\d{2}) (\d{2}):(\d{2})/.test(dt))
    .sort();
}

// Motion per artist per date: {artistKey: {YYYY_MM_DD: value}}, so the
// per-row lookups are O(1). Motion is NGuage boosted or docked by its 1-month
// growth (Backend nguage_service.motion_score), stored on each history point
// by the daily collector; points scored before Motion existed fall back to
// their NGuage. Everything the site labels "Motion" reads through here.
export function buildNguageByArtist(nguageHistory) {
  const m = {};
  for (const [id, pts] of Object.entries(nguageHistory || {})) {
    const byDate = {};
    for (const p of (pts || [])) {
      const v = p && (p.motion != null ? p.motion : p.value);
      if (p && p.date != null && v != null) byDate[p.date] = v;
    }
    m[id] = byDate;
  }
  return m;
}

// daily_update's "Name (key)" headings, plus a placeholder heading for each
// artists_collection artist missing from it. Membership is checked by artist
// KEY, not heading text: right after a rename the profile carries the new
// name while daily_update still files the artist under the old one, so a
// text check added a second "NewName (key)" row for the same artist.
export function mergeArtistHeadings(data, artistsCollection) {
  const names = Object.keys(data || {});
  const keys = new Set(names.map((h) => parseArtistHeading(h).key));
  Object.keys(artistsCollection || {}).forEach((key) => {
    const artistName = artistsCollection[key]?.name;
    if (artistName && artistName !== '' && !keys.has(key)) {
      keys.add(key);
      names.push(`${artistName} (${key})`);
    }
  });
  return names;
}

// Display name for a heading: the profile's current name when there is one
// (a rename shows up immediately), else the name the heading was filed under.
export function displayName(artistsCollection, heading) {
  const { name, key } = parseArtistHeading(heading);
  return (key && artistsCollection?.[key]?.name) || name;
}

// One artist's NGuage at each date-time (nguage_history is keyed by date, not
// date-time, so this matches on the date part); null where there's none.
// Number("garbage") is NaN, not null, so NaN is mapped to null here too -
// otherwise it slips past the null filter and poisons the sort.
export function nguageValues(byDate, dateTimes) {
  return dateTimes.map((dateTime) => {
    const v = byDate?.[dateTime.split(' ')[0]];
    return (v === undefined || v === null || Number.isNaN(Number(v))) ? null : Number(v);
  });
}

export const validNguage = (values) => values.filter((v) => v !== null && !Number.isNaN(v));

// The leaderboard's % change for one artist over `timeline`, given the
// artist's non-null NGuage values in that window (oldest first). Returns
// { percentNum, percentChange }: percentNum is null (shown "N/A", sinks in the
// Change sort) when there's nothing trustworthy to compare.
export function timelineChange(profile, validValues, timeline) {
  const none = { percentNum: null, percentChange: 'N/A' };
  const pct = (n) => ({ percentNum: n, percentChange: `${n >= 0 ? '+' : ''}${n.toFixed(1)}%` });

  // Like-for-like change from the collector (2D / 1W / 1M).
  const lfl = likeForLikeChange(profile, timeline);
  if (lfl !== undefined) return lfl === null ? none : pct(lfl.pct);

  // Otherwise (1Y / ALL): percentage change directly from stored values.
  if (validValues.length >= 2 && validValues[0] !== 0) {
    const oldValue = validValues[0];
    const newValue = validValues[validValues.length - 1];
    // Thin-data guard: a single-signal or tiny-baseline artist posts wild %
    // swings that shouldn't rank as movement. Only gate on a KNOWN-thin
    // signal count; a missing field is our data gap, not grounds to penalize
    // (same principle as NGuage itself).
    const signalCount = profile ? profile.nguage_signal_count : undefined;
    const knownThin = typeof signalCount === 'number' && signalCount < MIN_CHANGE_SIGNALS;
    if (oldValue < MIN_BASELINE_NGUAGE || knownThin) return none;
    return pct(((newValue - oldValue) / oldValue) * 100);
  }
  // A single point hasn't moved yet. An old value of 0 has no percentage to
  // show (0/0 is NaN, x/0 Infinity) - "N/A", not a NaN%/Infinity% artifact.
  if (validValues.length === 1) return { percentNum: 0.0, percentChange: '0.0%' };
  return none;
}

// All artists with positive 1-week movement, sorted biggest % gain first.
// Pinned to "1W" regardless of the leaderboard's selected timeline and
// computed over all artists, not search results. Feeds the homepage's
// "Trending this week" (top 6 overall) and the per-user bookmark-movers
// banner (filtered to the viewer's own co-signs).
export function computeWeeklyGainers({ artistNames, allDateTimes, artistsCollection, nguageByArtist }) {
  const weekDateTimes = getFilteredDataForTimeline(allDateTimes, '1W');
  if (weekDateTimes.length === 0) return [];

  const gainers = artistNames.map((artistHeading) => {
    const { key: artistKey } = parseArtistHeading(artistHeading);
    let percentNum;
    let pointNum;   // absolute NGuage-point gain
    const lfl = likeForLikeChange(artistsCollection[artistKey], '1W');
    if (lfl !== undefined) {
      if (lfl === null) return null;
      percentNum = lfl.pct;
      pointNum = lfl.to - lfl.from;
    } else {
      const validValues = validNguage(nguageValues(nguageByArtist[artistKey], weekDateTimes));
      if (validValues.length < 2) return null;

      const oldValue = validValues[0];
      const newValue = validValues[validValues.length - 1];
      // No size or platform-count floor: small artists growing fast are
      // exactly who this widget is for. Only a zero start has no % change.
      if (oldValue <= 0) return null;

      percentNum = ((newValue - oldValue) / oldValue) * 100;
      pointNum = newValue - oldValue;
    }
    if (pointNum <= 0) return null;

    const profile = artistKey ? artistsCollection[artistKey] : null;
    return {
      artistName: displayName(artistsCollection, artistHeading),
      artistKey,
      homeTown: profile
        ? (profile.hometown || profile.home_town || profile.city || 'N/A')
        : 'N/A',
      // Avatar URL that /api/artists overlays onto each record (claimant's
      // uploaded photo, else the stored Soundcharts image - see
      // artist_overlay_service); absent when the artist has none.
      photoURL: profile?.photoURL || null,
      percentNum,
      pointNum,
      percentChange: `+${percentNum.toFixed(1)}%`,
    };
  }).filter(Boolean);

  // Rank by % growth so the fastest growers lead whatever their size - a
  // small artist going 4 -> 8 out-ranks a big one going 80 -> 85. Points
  // only break ties.
  gainers.sort((a, b) => (b.percentNum - a.percentNum) || (b.pointNum - a.pointNum));
  return gainers;
}
